import { isIdle } from '@wix/toolbelt';
import { Status, } from './domain';
import { uuid, deepEqual, copy, toActivity } from './utils';
import { PresenceService } from '@wix/ambassador-presence-service/http';
import { INIT_BATCH_TIME, KEEP_ALIVE_TIME, PRESENCE_URL, WATCH_GROUP_TIME, } from './constants';
var TrackState;
(function (TrackState) {
    TrackState[TrackState["INIT"] = 0] = "INIT";
    TrackState[TrackState["SUBSCRIBED"] = 1] = "SUBSCRIBED";
})(TrackState || (TrackState = {}));
var UpdateType;
(function (UpdateType) {
    UpdateType[UpdateType["NONE"] = 0] = "NONE";
    UpdateType[UpdateType["STATE"] = 1] = "STATE";
    UpdateType[UpdateType["ACTIVITY"] = 2] = "ACTIVITY";
})(UpdateType || (UpdateType = {}));
const IDLE_TIME = 1000 * 60 * 10; // 10 min
export default class Group {
    constructor({ getPresenceConnection, scope, getSignedInstance, initialState = {}, keepAliveInterval = KEEP_ALIVE_TIME, isIdleMs, onIdle, onActive, }) {
        this.onStateChangeFn = () => { };
        this.sessionId = uuid();
        this.trackState = TrackState.INIT;
        this.lastInactivity = null;
        this.list = [];
        this.onDisconnectFn = () => { };
        this.shuoldWatchGroup = true;
        this.onIdle = () => {
            this.lastInactivity = Date.now() - this.isIdleMs;
            this.refreshState();
            this.onIdleFn();
        };
        this.onActive = () => {
            this.lastInactivity = null;
            this.refreshState();
            this.onActiveFn();
        };
        this.watchGroup = async () => {
            if (this.shuoldWatchGroup) {
                (await this.presenceClient())
                    .watch({ scope: this.scope })
                    .catch(() => { });
            }
            if (this.watchGroupTimer) {
                clearTimeout(this.watchGroupTimer);
            }
            this.watchGroupTimer = setTimeout(() => this.watchGroup(), WATCH_GROUP_TIME);
        };
        this.onSubscribe = async () => {
            this.list = [];
            this.trackState = TrackState.INIT;
            if (this.initBatchTimer) {
                clearTimeout(this.initBatchTimer);
            }
            this.watchGroup();
            // we await in order to get a better chance to get
            // that SetPresence update on the initial batch
            await this.keepAlive();
            this.idle.listen();
            this.initBatchTimer = setTimeout(() => {
                this.trackState = TrackState.SUBSCRIBED;
                this.onStateChange();
            }, INIT_BATCH_TIME);
        };
        this.handleStateChanges = (presentity) => {
            // in case Presence's GC kicks in - auto offline
            // sends the event with no data at all
            presentity.data = presentity.data || {};
            const updateType = this.updateList(presentity);
            if (this.trackState === TrackState.SUBSCRIBED &&
                updateType === UpdateType.STATE) {
                this.onStateChange(presentity);
            }
        };
        this.getPresenceConnection = getPresenceConnection;
        this.getSignedInstance = getSignedInstance;
        this.keepAliveInterval = keepAliveInterval;
        this.scope = scope;
        this.storeState(initialState);
        this.isIdleMs = window.__PRESENCE_IDLE_TIME ?? isIdleMs ?? IDLE_TIME;
        this.onIdleFn = onIdle ?? (() => { });
        this.onActiveFn = onActive ?? (() => { });
        this.idle = isIdle({
            isIdleTime: this.isIdleMs,
            onIdle: this.onIdle,
            onActive: this.onActive,
        });
    }
    async initPresenceApi() {
        if (!this.presenceApi) {
            this.presenceApi = PresenceService(PRESENCE_URL, {
                ignoredProtoHttpUrlPart: '/api',
            }).PresenceService();
        }
    }
    track(onStateChange, opts) {
        this.onStateChangeFn = onStateChange;
        this.shuoldWatchGroup = opts?.watchGroup ?? this.shuoldWatchGroup;
        this.connection = this.getPresenceConnection();
        const info = {
            presenceSessionId: this.sessionId,
            watchGroup: opts?.watchGroup,
        };
        this.connection.on('@duplexer:disconnected', this.onDisconnectFn);
        return this.connection
            .subscribe(this.scope, { info })
            .on('@duplexer:subscription_succeeded', this.onSubscribe)
            .on('update-state', this.handleStateChanges);
    }
    disconnect() {
        if (this.connection) {
            this.connection.disconnect();
            this.connection = undefined;
        }
    }
    onDisconnect(onDisconnectFn) {
        this.onDisconnectFn = onDisconnectFn;
    }
    async setState(state) {
        this.storeState(state);
        if (this.connection) {
            await (await this.presenceClient()).setData({
                sessionId: this.sessionId,
                scope: this.scope,
                data: this.state,
            });
        }
    }
    getSessionId() {
        return this.sessionId;
    }
    async emptyGroup() {
        await (await this.presenceClient()).resetScope({
            scope: this.scope,
        });
    }
    storeState(state) {
        this.state = {
            ...state,
            __lastInactivity: this.lastInactivity,
        };
    }
    refreshState() {
        return this.setState(this.state);
    }
    async keepAlive() {
        if (!this.connection) {
            clearTimeout(this.keepAliveTimer);
            return;
        }
        await this.refreshState().catch(() => { });
        if (this.keepAliveTimer) {
            clearTimeout(this.keepAliveTimer);
        }
        this.keepAliveTimer = setTimeout(() => this.keepAlive(), this.keepAliveInterval);
    }
    onStateChange(presentity) {
        this.onStateChangeFn(copy(this.list), presentity ? copy(presentity) : undefined);
    }
    updateList(presentity) {
        const prev = this.list.find((p) => p.userId === presentity.userId && p.sessionId === presentity.sessionId);
        presentity.activity = toActivity(presentity);
        // when someone starts watching a group, the entire group
        // is being sent to everyone. we may want to solve it.
        if (prev && deepEqual(prev, presentity)) {
            return UpdateType.NONE;
        }
        if (prev) {
            this.list = this.list.filter((p) => p !== prev);
        }
        if (presentity.status === Status.ONLINE) {
            this.list.push(presentity);
        }
        return UpdateType.STATE;
    }
    async presenceClient() {
        await this.initPresenceApi();
        return this.presenceApi({
            Authorization: this.getSignedInstance(),
        });
    }
}
//# sourceMappingURL=group.js.map